import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, fecha, hoy } from '../utils/formato';

// A quién cobrarle, y registrar lo que pagó.
//
// Ordenada por el más atrasado, no por nombre ni por inmueble: el de arriba es
// a quien hay que llamar hoy.
//
// El pago pide SIEMPRE qué mes cubre. Es un campo más que teclear, y es lo que
// permite que el programa sepa cuáles meses faltan en vez de llevar solo un
// saldo. Con un saldo suelto no hay cómo saber si debe tres meses seguidos o
// uno de hace un año.

const mesesDe = (desde, hasta) => {
  const lista = [];
  const [a0, m0] = desde.split('-').map(Number);
  const [a1, m1] = hasta.split('-').map(Number);
  for (let i = a0 * 12 + m0 - 1; i <= a1 * 12 + m1 - 1; i++) {
    lista.push(`${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`);
  }
  return lista;
};

const nombreMes = (p) => {
  const [a, m] = p.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, 1))
    .toLocaleDateString('es-CO', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

function Cobrar({ onCambio }) {
  const [datos, setDatos] = useState({ contratos: [], total: 0, en_mora: 0 });
  const [cobrando, setCobrando] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/cobrar').then(r => setDatos(r.data || { contratos: [], total: 0 }))
      .catch(() => {});
  }, []);

  useEffect(cargar, [cargar]);

  const abrir = (c) => {
    const e = c.estado_cuenta;
    // Sale marcado el mes más viejo sin cubrir: es el que hay que cobrar
    // primero, y es el que la gente olvida.
    const periodo = e.desde || hoy().slice(0, 7);
    const pendiente = (e.detalle || []).find(d => d.periodo === periodo);
    setCobrando({
      contrato: c, periodo, fecha: hoy(),
      monto: pendiente ? String(pendiente.saldo) : String(e.canon_vigente),
      forma_pago: 'transferencia', concepto: 'arriendo', nota: '',
    });
  };

  const guardar = async (e) => {
    e.preventDefault();
    const monto = parseFloat(cobrando.monto);
    if (!(monto > 0)) return setAviso({ tipo: 'malo', texto: 'El pago tiene que ser mayor que cero' });
    setGuardando(true);
    try {
      const { data } = await axios.post('/api/pagos', {
        contrato_id: cobrando.contrato.id,
        fecha: cobrando.fecha, periodo: cobrando.periodo, monto,
        forma_pago: cobrando.forma_pago, concepto: cobrando.concepto,
        nota: cobrando.nota || null,
      });
      const q = data.estado_cuenta.saldo;
      setAviso({
        tipo: 'bueno',
        texto: q > 0
          ? `${cobrando.contrato.arrendatario} pagó ${plata(monto)} — le quedan debiendo ${plata(q)}`
          : `${cobrando.contrato.arrendatario} pagó ${plata(monto)} y quedó al día`,
      });
      setCobrando(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo registrar el pago' });
    }
    setGuardando(false);
  };

  const ver = async (id) => {
    try {
      const { data } = await axios.get(`/api/contratos/${id}`);
      setDetalle(data);
    } catch {
      setAviso({ tipo: 'malo', texto: 'No se pudo cargar el detalle' });
    }
  };

  return (
    <>
      <div className="encabezado">
        <h1>Cobrar</h1>
        <p>Quién debe, cuántos meses y desde cuándo</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      <div className="rejilla">
        <div className="dato" style={{ '--color': datos.total > 0 ? 'var(--ojo)' : 'var(--bueno)' }}>
          <div className="rotulo">📥 Por cobrar</div>
          <div className="valor cifra">{plata(datos.total)}</div>
          <div className="pie">
            {datos.contratos.length === 1 ? '1 contrato debe'
              : `${datos.contratos.length} contratos deben`}
          </div>
        </div>
        <div className="dato" style={{ '--color': datos.en_mora ? 'var(--malo)' : 'var(--bueno)' }}>
          <div className="rotulo">⏰ En mora</div>
          <div className="valor cifra">{datos.en_mora}</div>
          <div className="pie">
            pasados el día de pago y {datos.dias_gracia ?? 5} días de gracia
          </div>
        </div>
      </div>

      <div className="bloque">
        <h2>A quién cobrarle
          {datos.contratos.length > 0 && <span className="contador">{datos.contratos.length}</span>}
        </h2>
        {datos.contratos.length === 0 ? (
          <div className="vacio">
            Nadie debe nada. Cuando pase un mes sin pagar, el contrato aparece aquí.
          </div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Inmueble</th>
                  <th>Arrendatario</th>
                  <th style={{ textAlign: 'right' }}>Canon</th>
                  <th style={{ textAlign: 'right' }}>Debe</th>
                  <th>Desde</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {datos.contratos.map(c => {
                  const e = c.estado_cuenta;
                  return (
                    <tr key={c.id}>
                      <td className="recio">
                        {c.inmueble}
                        <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                          {c.inmueble_tipo}
                        </div>
                      </td>
                      <td>
                        {c.arrendatario}
                        {c.arrendatario_telefono && (
                          <div className="cifra" style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                            📞 {c.arrendatario_telefono}
                          </div>
                        )}
                      </td>
                      <td className="num">{plata(e.canon_vigente)}</td>
                      <td className="num recio" style={{ color: c.mora ? 'var(--malo)' : undefined }}>
                        {plata(e.saldo)}
                        <div style={{ fontSize: 11, fontWeight: 400 }}>
                          {e.meses_pendientes} {e.meses_pendientes === 1 ? 'mes' : 'meses'}
                        </div>
                      </td>
                      <td>
                        <span className={'pinta ' + (c.mora ? 'malo' : 'gris')}>
                          {e.desde ? nombreMes(e.desde) : '—'}
                        </span>
                        {c.mora && (
                          <div style={{ fontSize: 11, color: 'var(--malo)', marginTop: 3 }}>
                            en mora
                          </div>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="boton chico" onClick={() => abrir(c)}>💵 Pagó</button>{' '}
                        <button className="boton chico suave" onClick={() => ver(c.id)}>Ver</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Registrar un pago ─────────────────────────────────────────────── */}
      {cobrando && (
        <div className="bloque">
          <h2>💵 Pago de {cobrando.contrato.arrendatario}</h2>
          <form onSubmit={guardar}>
            <div className="cuerpo">
              <div className="aviso ojo" style={{ marginBottom: 16 }}>
                {cobrando.contrato.inmueble} · debe{' '}
                <strong>{plata(cobrando.contrato.estado_cuenta.saldo)}</strong> en{' '}
                {cobrando.contrato.estado_cuenta.meses_pendientes}{' '}
                {cobrando.contrato.estado_cuenta.meses_pendientes === 1 ? 'mes' : 'meses'}
              </div>

              <div className="campos">
                <label className="campo">
                  <span>Qué mes está pagando *</span>
                  <select value={cobrando.periodo}
                          onChange={e => {
                            const d = (cobrando.contrato.estado_cuenta.detalle || [])
                              .find(x => x.periodo === e.target.value);
                            setCobrando({ ...cobrando, periodo: e.target.value,
                              monto: d ? String(d.saldo) : cobrando.monto });
                          }}>
                    {mesesDe(
                      String(cobrando.contrato.fecha_inicio).slice(0, 7),
                      hoy().slice(0, 7)
                    ).reverse().map(p => {
                      const d = (cobrando.contrato.estado_cuenta.detalle || [])
                        .find(x => x.periodo === p);
                      const falta = d && d.saldo > 0;
                      return (
                        <option key={p} value={p}>
                          {nombreMes(p)}{falta ? ` — falta ${plata(d.saldo)}` : ' — al día'}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label className="campo">
                  <span>Cuánto pagó *</span>
                  <input type="number" step="1" min="0" className="num-grande" required autoFocus
                         value={cobrando.monto}
                         onChange={e => setCobrando({ ...cobrando, monto: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Fecha del pago</span>
                  <input type="date" value={cobrando.fecha}
                         onChange={e => setCobrando({ ...cobrando, fecha: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Cómo pagó</span>
                  <select value={cobrando.forma_pago}
                          onChange={e => setCobrando({ ...cobrando, forma_pago: e.target.value })}>
                    <option value="transferencia">🏦 Transferencia</option>
                    <option value="efectivo">💵 Efectivo</option>
                  </select>
                </label>
                <label className="campo">
                  <span>Concepto</span>
                  <select value={cobrando.concepto}
                          onChange={e => setCobrando({ ...cobrando, concepto: e.target.value })}>
                    <option value="arriendo">Arriendo</option>
                    <option value="deposito">Depósito</option>
                    <option value="servicios">Servicios</option>
                    <option value="otro">Otro</option>
                  </select>
                </label>
              </div>

              {cobrando.concepto === 'deposito' && (
                <div className="aviso" style={{ marginTop: 12 }}>
                  El depósito <strong>no cuenta como arriendo pagado</strong>: es plata
                  suya que hay que devolver al terminar el contrato.
                </div>
              )}

              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setCobrando(null)}>
                  Cancelar
                </button>
                <button className="boton" disabled={guardando}>
                  {guardando ? 'Guardando…' : '💾 Registrar pago'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ── Detalle mes a mes ─────────────────────────────────────────────── */}
      {detalle && (
        <div className="bloque">
          <h2>📄 {detalle.inmueble} · {detalle.arrendatario}</h2>
          <div className="cuerpo" style={{ paddingBottom: 0 }}>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
              Mes por mes, desde que empezó el contrato. Así se puede mostrar de
              dónde sale lo que debe cuando lo pregunte.
            </p>
          </div>
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Mes</th>
                  <th style={{ textAlign: 'right' }}>Debía</th>
                  <th style={{ textAlign: 'right' }}>Pagó</th>
                  <th style={{ textAlign: 'right' }}>Falta</th>
                </tr>
              </thead>
              <tbody>
                {[...(detalle.estado_cuenta.detalle || [])].reverse().map(d => (
                  <tr key={d.periodo}>
                    <td className="recio">{nombreMes(d.periodo)}</td>
                    <td className="num">{plata(d.debe)}</td>
                    <td className="num">{plata(d.pago)}</td>
                    <td className="num recio"
                        style={{ color: d.saldo > 0 ? 'var(--malo)' : 'var(--bueno)' }}>
                      {d.saldo > 0 ? plata(d.saldo) : '✓'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cuerpo">
            {detalle.estado_cuenta.deposito_recibido > 0 && (
              <p style={{ fontSize: 14 }}>
                Depósito recibido:{' '}
                <strong>{plata(detalle.estado_cuenta.deposito_recibido)}</strong>
                {' '}— se devuelve al terminar.
              </p>
            )}
            <div className="acciones">
              <button className="boton suave" onClick={() => setDetalle(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Cobrar;
